<script setup lang="ts">
/**
 * POST /deployments · POST /deployments/{slug}/revisions. A model version and, for a new
 * deployment, its name. Nothing is bound by hand: the request carries no knowledge-base field,
 * and under the contract an absent field is the instruction to inherit the documents of the
 * deployed model's dataset (founder, 2026-09-21). A deployment that needs a different selection,
 * or none at all, says so on its own page.
 */
import Dialog from '~/components/ui/Dialog.vue'
import Choice from '~/components/ui/Choice.vue'
import PickList from '~/components/ui/PickList.vue'
import Field from '~/components/ui/Field.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import StatusCell from '~/components/ui/StatusCell.vue'
import Skeleton from '~/components/ui/Skeleton.vue'

const props = withDefaults(defineProps<{
  /** `blocked` is why this version cannot be published, empty when it can. */
  models: { id: string; name: string; version: string; facts: string; status: string; blocked?: string }[]
  /** Set when adding a version to an existing deployment — the name is fixed then. */
  deployment?: { slug: string }
  /** The model the caller stands on (a conversation, a model page); falls back to the first one. */
  initialModelId?: string
  /** True until the project's models have answered. */
  loading?: boolean
}>(), { deployment: undefined, initialModelId: undefined, loading: false })
defineEmits<{ close: []; submit: [payload: Record<string, unknown>] }>()
/* A version the platform will refuse is never the settled choice: the dialog opens on one that
   can actually be published, and the refused ones stay visible with their reason (W8-UI-03). */
const publishable = computed(() => props.models.filter((entry) => !entry.blocked))
/* Arriving from a version that cannot be published leaves the dialog with nothing chosen and that
   version's reason on its row — never a quiet substitution of some other version for the one the
   customer pressed Deploy on. */
const firstPublishable = () => {
  const wanted = props.models.find((entry) => entry.id === props.initialModelId)
  if (wanted) return wanted.blocked ? '' : wanted.id
  return publishable.value[0]?.id ?? ''
}
const model = ref(firstPublishable())
const name = ref('')
const touched = ref(false)
/* The list arrives after the dialog opens; the first real answer settles a choice nobody made. */
watch(() => props.models, () => {
  if (touched.value) return
  model.value = firstPublishable()
})
/* Nothing in the project can be published: said once, above the list, so the reason is read
   before the press rather than after it. */
const noneAvailable = computed(() => props.models.length > 0 && publishable.value.length === 0)
/* What the waiting form reserves: the picker's scroller holds up to three rows before it scrolls,
   and only the read can say how many a project has. */
const RESERVED_VERSION_ROWS = 3
</script>

<template>
  <!-- The title names what the customer is making, in the one word the Deploy pages use for it.
       How it is served — an OpenAI-compatible address — is said where the address itself is
       shown, not in the title of the intention (founder, 2026-09-21, notes 36 and 37). -->
  <Dialog :title="deployment ? 'New revision' : 'New deployment'" :meta="deployment ? '/' + deployment.slug : undefined" :width="520" @close="$emit('close')">
    <template #icon><Icon name="deploy" /></template>

    <!-- The waiting form carries the rows the settled one has: the version picker and, for a new
         deployment, its name. `--measure-64px` was never declared, so these bars used to collapse
         to nothing and the dialog opened shorter than it ends up. -->
    <template v-if="loading">
      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-muted-foreground">Model version</span>
        <Choice v-for="row in RESERVED_VERSION_ROWS" :key="row" placeholder />
      </div>
      <div v-if="!deployment" class="flex flex-col gap-1.5">
        <span class="text-xs text-muted-foreground">Deployment name</span>
        <Skeleton height="var(--control-height)" />
        <Skeleton width="var(--measure-160px)" height="var(--skeleton-line)" />
      </div>
    </template>

    <template v-else>
    <div class="flex flex-col gap-1.5">
      <span class="text-xs text-muted-foreground">Model version</span>
      <!-- Searchable from the second entry: fifty trained versions are found by typing (note 38). -->
      <PickList v-slot="{ shown }" :items="models" unit="versions" :search-from="2" :match="(entry, query) => (entry.name + ' v' + entry.version + ' ' + entry.facts).toLowerCase().includes(query)" height="var(--measure-240px)" empty="Nothing is trained yet in this project.">
        <!-- A version that cannot be published says so where it is chosen, in its own row, and
             cannot be picked: the refusal is read before the press, not after it. -->
        <Choice v-for="entry in shown" :key="entry.id" :title="entry.name + ' v' + entry.version" :facts="entry.blocked || entry.facts" :disabled="Boolean(entry.blocked)" :selected="model === entry.id" @click="model = entry.id; touched = true">
          <span class="ml-auto shrink-0"><StatusCell :status="entry.blocked ? 'Cannot be published' : entry.status" :tone="entry.blocked ? 'refusal' : undefined" /></span>
        </Choice>
      </PickList>
      <p v-if="noneAvailable" class="m-0 text-xs leading-relaxed text-pretty text-muted-foreground">No version in this project can be published yet. Train a run that finishes with a usable result, and it becomes available here.</p>
    </div>

    <Field v-if="!deployment" v-model="name" label="Deployment name" placeholder="support-assistant" hint="Blank uses the model name. The name becomes the address callers use, and never changes afterwards." />

    </template>

    <template v-if="loading" #actions><ActionButton label="Cancel" @click="$emit('close')" /></template>
    <template v-else #actions>
      <ActionButton label="Cancel" @click="$emit('close')" />
      <ActionButton :label="deployment ? 'Create revision' : 'Create deployment'" variant="primary" :disabled="!model" @click="$emit('submit', { name: name.trim(), modelId: model })"><template #icon><Icon name="deploy" /></template></ActionButton>
    </template>
  </Dialog>
</template>
